<script setup lang="ts">
import gpsValidation from '@/shared/Containment/modules/gps';

const { isAuthenticated } = storeToRefs(useAuthStore());
const { isGeoLocationVisitorEnabled } = useFeatureFlagsJson();

const isActiveGeoLocationEnabled = computed<boolean>(() => {
  return !isAuthenticated.value && isGeoLocationVisitorEnabled.value;
});

const isGeoLocationGranted = ref<boolean>(false);

const isFirstAttempt = useCookie('geolocation-pristine', {
  default: () => true,
  expires: new Date(new Date().setDate(new Date().getDate() + 7)),
});

const isGeoLocationDialogVisible = computed<boolean>(() => {
  return !isGeoLocationGranted.value;
});

async function fetchGeolocationState() {
  if (!isFirstAttempt.value) {
    const isValid = await gpsValidation();

    isGeoLocationGranted.value = isValid;

    return;
  }

  // Permissions API não existe no WebKit do iOS < 16. Sem ela, assume
  // que precisa pedir permissão e segue o fluxo padrão.
  if (!navigator.permissions?.query) {
    isGeoLocationGranted.value = false;
    isFirstAttempt.value = false;

    return;
  }

  try {
    const { state } = await navigator.permissions.query({
      name: 'geolocation',
    });

    isGeoLocationGranted.value = state === 'granted';
  } catch {
    isGeoLocationGranted.value = false;
  }

  isFirstAttempt.value = false;
}

onMounted(async () => {
  await fetchGeolocationState();
});

onActivated(async () => {
  await fetchGeolocationState();
});
</script>

<template>
  <template v-if="isActiveGeoLocationEnabled">
    <div
      v-show="isGeoLocationDialogVisible"
      :class="$style.geoLocationPermission"
    >
      <div :class="$style.contentInner">
        <AuthVerificationGps @on-refresh="fetchGeolocationState" />
      </div>
    </div>
  </template>
</template>

<style module lang="scss">
.geoLocationPermission {
  @apply fixed top-0 left-0 w-full h-full z-[999] bg-bg-primary/95 flex items-center justify-center;

  .contentInner {
    @apply max-w-[500px] w-full m-auto;
  }

  &:after {
    @apply content-[''] w-full h-full bg-bg-primary/50 absolute top-0 left-0 -z-10;
  }
}
</style>
